import React from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
import { ui, contact } from '../data/content';

const InfoCard = ({ icon, title, children, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }} transition={{ delay }}
    className="glass-card antique-corner contact-pulse"
    style={{ borderRadius: '16px', padding: '28px 24px', borderColor: `${color}40` }}>
    <div style={{
      width: '48px', height: '48px', borderRadius: '12px', marginBottom: '16px',
      background: `${color}18`, border: `1px solid ${color}40`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <i className={`fa-solid ${icon}`} style={{ fontSize: '20px', color, filter: `drop-shadow(0 0 5px ${color})` }} />
    </div>
    <h3 style={{ fontFamily: '"Cinzel Decorative", serif', fontSize: '13px', color, letterSpacing: '1.5px', margin: '0 0 12px', textTransform: 'uppercase' }}>
      {title}
    </h3>
    {children}
  </motion.div>
);

export default function ContactSection() {
  const { lang } = useLang();

  return (
    <section id="contact" data-testid="contact-section"
      style={{
        background: 'linear-gradient(180deg, #0A1128 0%, #0d1730 100%)',
        padding: '80px 20px 60px', position: 'relative', overflow: 'hidden',
      }}>

      {/* BG glow */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at 50% 100%, rgba(255,153,0,0.06) 0%, transparent 60%)',
      }} />

      <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* Section heading */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <p style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', letterSpacing: '5px', color: '#FF9933', textTransform: 'uppercase', marginBottom: '8px' }}>
            — Get In Touch —
          </p>
          <h2 className="hindi divine-glow-text"
            style={{ fontFamily: '"Yatra One", cursive', fontSize: 'clamp(24px,5vw,40px)', color: '#FFD700', margin: '0 0 10px' }}>
            {ui.contact.title[lang]}
          </h2>
          <div className="section-divider">
            <span style={{ color: '#FFD700', fontSize: '16px' }}>✦</span>
            <span style={{ fontSize: '13px', color: 'rgba(255,243,176,0.5)', fontFamily: 'Khand, sans-serif' }}>
              {lang === 'hi' ? 'हम आपकी सेवा में सदैव तत्पर हैं' : 'We are always ready to serve you'}
            </span>
            <span style={{ color: '#FFD700', fontSize: '16px' }}>✦</span>
          </div>
        </div>

        {/* Contact cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginBottom: '36px' }}>

          {/* Address */}
          <InfoCard icon="fa-location-dot" title={ui.contact.address[lang]} color="#FFD700" delay={0.1}>
            <p className="hindi-body" style={{ color: '#FFF3B0', fontSize: '14px', lineHeight: 1.7, margin: 0, fontFamily: 'Khand, sans-serif' }}>
              {lang === 'hi' ? contact.addressHi : contact.addressEn}
            </p>
          </InfoCard>

          {/* Phone + WhatsApp */}
          <InfoCard icon="fa-phone-volume" title={ui.contact.phone[lang]} color="#FF9933" delay={0.2}>
            <a href={`tel:${contact.phone}`} data-testid="contact-phone-link"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', marginBottom: '12px' }}>
              <i className="fa-solid fa-phone" style={{ color: '#FF9933', fontSize: '14px' }} />
              <span style={{ color: '#FFF3B0', fontSize: '18px', fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '1px' }}>
                {contact.phone}
              </span>
            </a>
            <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="btn-whatsapp"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 20px', borderRadius: '25px', textDecoration: 'none', fontSize: '14px', fontFamily: 'Rajdhani, sans-serif' }}>
              <i className="fa-brands fa-whatsapp" style={{ fontSize: '16px' }} />
              WhatsApp
            </a>
          </InfoCard>

          {/* Email */}
          <InfoCard icon="fa-envelope" title={ui.contact.email[lang]} color="#50C878" delay={0.3}>
            <a href={`mailto:${contact.email}`} data-testid="contact-email-link"
              style={{ color: '#50C878', textDecoration: 'none', fontFamily: 'Rajdhani, sans-serif', fontSize: '14px', wordBreak: 'break-all' }}>
              {contact.email}
            </a>
            <p style={{ color: 'rgba(255,243,176,0.5)', fontSize: '12px', fontFamily: 'Khand, sans-serif', marginTop: '8px' }}>
              {lang === 'hi' ? 'कार्य समय: सोम-शनि, 10am – 7pm' : 'Working hours: Mon-Sat, 10am – 7pm'}
            </p>
          </InfoCard>
        </div>

        {/* About section */}
        <div id="about" style={{ marginBottom: '36px' }}>
          <div className="glass-card antique-corner" style={{ borderRadius: '16px', padding: '28px 32px', textAlign: 'center' }}>
            <h3 className="hindi divine-glow-text" style={{ fontFamily: '"Yatra One", cursive', fontSize: 'clamp(18px,3vw,26px)', color: '#FFD700', marginBottom: '14px' }}>
              {lang === 'hi' ? 'श्री रणजीत सर्विसेस के बारे में' : 'About Shri Ranjeet Services'}
            </h3>
            <p className="hindi-body" style={{ fontSize: 'clamp(13px,1.8vw,15px)', color: '#FFF3B0', lineHeight: 1.8, maxWidth: '720px', margin: '0 auto', fontFamily: 'Khand, sans-serif' }}>
              {lang === 'hi'
                ? 'श्री रणजीत सर्विसेस, इंदौर में स्थित एक विश्वसनीय मल्टी-सर्विस केंद्र है जो सरकारी सेवाएँ, RTO कार्य, व्यवसाय पंजीकरण, बीमा, यात्रा, डिजिटल डिज़ाइन एवं वेबसाइट सेवाएँ प्रदान करता है। हमारा उद्देश्य है — हर खुशी को डिजिटल पहचान देना।'
                : 'Shri Ranjeet Services is a trusted multi-service center in Indore offering government services, RTO works, business registration, insurance, travel, digital design & website services. Our mission — to give every happiness a digital identity.'}
            </p>
          </div>
        </div>

        {/* Google Map */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,215,0,0.2)', marginBottom: '36px' }}>
          <iframe
            data-testid="google-map"
            title="Shri Ranjeet Services Location"
            src={contact.mapSrc}
            width="100%" height="300" style={{ border: 0, display: 'block', filter: 'brightness(0.85) saturate(0.8) hue-rotate(200deg)' }}
            allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>

        {/* Quick contact bar */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer"
              data-testid="contact-wa-btn"
              className="btn-whatsapp"
              style={{ padding: '12px 28px', borderRadius: '50px', textDecoration: 'none', fontSize: '15px', fontFamily: 'Rajdhani, sans-serif', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fa-brands fa-whatsapp" style={{ fontSize: '18px' }} />
              {ui.hero.whatsapp[lang]}
            </a>
            <a href={`tel:${contact.phone}`}
              data-testid="contact-call-btn"
              className="btn-contact"
              style={{ padding: '12px 28px', borderRadius: '50px', textDecoration: 'none', fontSize: '15px', fontFamily: 'Rajdhani, sans-serif', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fa-solid fa-phone" style={{ fontSize: '16px' }} />
              {ui.hero.callNow[lang]}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
